import { useState, useEffect, useCallback } from "react";
import { message } from "antd";
import { useAuth } from "../../../hooks/useAuth";
import api from "../../../api/enhancedApi";

/**
 * Dashboard 布局管理 Hook
 * 遵循 API_GUIDELINES.md 规范
 */
export const useDashboardLayout = () => {
    const { user } = useAuth();
    const [layout, setLayout] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [layoutSource, setLayoutSource] = useState("system_default");

    // 获取布局
    const fetchLayout = useCallback(async () => {
        if (!user) return;

        try {
            setLoading(true);
            setError(null);

            // 使用增强的 API 服务
            // dashboardApi.getLayout() 返回 response.data,所以 response 就是数据本身
            const data = await api.dashboard.getLayout();

            if (data) {
                setLayout(data.layout);
                setLayoutSource(data.source);
            }
        } catch (err) {
            console.error("Failed to fetch dashboard layout:", err);
            setError(err);
            // 错误提示由 axios 拦截器统一处理,这里只处理特定业务逻辑
        } finally {
            setLoading(false);
        }
    }, [user]);

    // 保存布局
    const saveLayout = useCallback(async (newLayout) => {
        try {
            // 仅提取必要字段发送给后端
            const sanitizedLayout = newLayout.map((item) => ({
                i: item.i,
                x: item.x,
                y: item.y,
                w: item.w,
                h: item.h,
            }));

            // 使用增强的 API 服务
            // dashboardApi.updateLayout() 返回 response.data,所以 response 就是数据本身
            const data = await api.dashboard.updateLayout(sanitizedLayout);

            // 按照 API_GUIDELINES.md 规范,检查 success 字段
            if (data && data.success) {
                setLayout(sanitizedLayout);
                setLayoutSource("user_custom");
                message.success(data.message || "布局保存成功");
            } else {
                message.error(data?.message || "保存布局失败");
            }
        } catch (err) {
            console.error("Failed to save dashboard layout:", err);
            // 错误提示由 axios 拦截器统一处理
        }
    }, []);

    // 重置布局
    const resetLayout = useCallback(async () => {
        try {
            const data = await api.dashboard.resetLayout();
            if (data && data.success) {
                message.success(data.message || "布局已重置");
                // 重置后重新获取布局
                fetchLayout();
            } else {
                message.error(data?.message || "重置布局失败");
            }
        } catch (err) {
            console.error("Failed to reset dashboard layout:", err);
        }
    }, [fetchLayout]);

    // 初始加载
    useEffect(() => {
        fetchLayout();
    }, [fetchLayout]);

    return {
        layout,
        loading,
        error,
        layoutSource,
        saveLayout,
        resetLayout,
        refreshLayout: fetchLayout,
    };
};
